import React, {useState, useCallback} from "react";
import {Dropdown, Menu, Modal, Tooltip} from "antd";
import {
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileZipOutlined,
  FileOutlined,
  MoreOutlined,
  DownloadOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import moment from "moment";

const ICON_MAP = {
  pdf: {icon: FilePdfOutlined, color: "#ff4d4f"},
  jpg: {icon: FileImageOutlined, color: "#1890ff"},
  jpeg: {icon: FileImageOutlined, color: "#1890ff"},
  png: {icon: FileImageOutlined, color: "#1890ff"},
  gif: {icon: FileImageOutlined, color: "#1890ff"},
  bmp: {icon: FileImageOutlined, color: "#1890ff"},
  webp: {icon: FileImageOutlined, color: "#1890ff"},
  svg: {icon: FileImageOutlined, color: "#1890ff"},
  doc: {icon: FileWordOutlined, color: "#2b579a"},
  docx: {icon: FileWordOutlined, color: "#2b579a"},
  xls: {icon: FileExcelOutlined, color: "#217346"},
  xlsx: {icon: FileExcelOutlined, color: "#217346"},
  csv: {icon: FileExcelOutlined, color: "#217346"},
  ppt: {icon: FilePptOutlined, color: "#d24726"},
  pptx: {icon: FilePptOutlined, color: "#d24726"},
  zip: {icon: FileZipOutlined, color: "#faad14"},
  rar: {icon: FileZipOutlined, color: "#faad14"},
  "7z": {icon: FileZipOutlined, color: "#faad14"},
};

const PREVIEWABLE = ["pdf", "jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];

function getFileIcon(type) {
  const entry = ICON_MAP[(type || "").toLowerCase()];
  if (entry) return entry;
  return {icon: FileOutlined, color: "#8c8c8c"};
}

function formatSize(bytes) {
  if (!bytes) return "-";
  const k = 1024;
  const sizes = ["o", "Ko", "Mo", "Go"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function hasExtension(name) {
  if (!name) return false;
  const parts = name.split(".");
  return parts.length > 1 && parts[parts.length - 1].length > 0;
}

const FileList = ({files, onDownload, onPreview, onRename, onDelete, isAdmin, onReorder}) => {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const onDragStart = useCallback((e, idx) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/file-idx", String(idx));
    setDragIdx(idx);
  }, []);

  const onDragOver = useCallback((e, idx) => {
    if (dragIdx === null) return;
    e.preventDefault();
    if (overIdx !== idx) setOverIdx(idx);
  }, [dragIdx, overIdx]);

  const onDragEnd = useCallback(() => {
    if (dragIdx !== null && overIdx !== null && dragIdx !== overIdx) {
      onReorder(dragIdx, overIdx);
    }
    setDragIdx(null);
    setOverIdx(null);
  }, [dragIdx, overIdx, onReorder]);

  if (!files || files.length === 0) {
    return <div className="file-grid-empty">Aucun fichier dans ce dossier</div>;
  }

  return (
    <div className="file-list">
      {files.map((file, idx) => {
        const {icon: IconComp, color} = getFileIcon(file.type);
        const canPreview = PREVIEWABLE.includes((file.type || "").toLowerCase());
        const noExt = !hasExtension(file.name);
        const isDragging = dragIdx === idx;
        const isDragOver = overIdx === idx && dragIdx !== idx;

        const menu = (
          <Menu onClick={(e) => e.domEvent.stopPropagation()}>
            <Menu.Item key="download" icon={<DownloadOutlined />} onClick={() => onDownload(file)}>
              Télécharger
            </Menu.Item>
            {canPreview && (
              <Menu.Item key="preview" icon={<EyeOutlined />} onClick={() => onPreview(file)}>
                Aperçu
              </Menu.Item>
            )}
            {isAdmin && (
              <Menu.Item key="rename" icon={<EditOutlined />} onClick={() => onRename(file)}>
                Renommer
              </Menu.Item>
            )}
            {isAdmin && (
              <Menu.Item
                key="delete"
                icon={<DeleteOutlined />}
                danger
                onClick={() => {
                  Modal.confirm({
                    title: "Confirmer la suppression",
                    content: `Supprimer "${file.name}" ?`,
                    okText: "Supprimer",
                    okType: "danger",
                    cancelText: "Annuler",
                    onOk: () => onDelete([file.path]),
                  });
                }}
              >
                Supprimer
              </Menu.Item>
            )}
          </Menu>
        );

        return (
          <div
            key={file.path}
            className="file-list-item"
            draggable={!!onReorder}
            onDragStart={onReorder ? (e) => onDragStart(e, idx) : undefined}
            onDragOver={onReorder ? (e) => onDragOver(e, idx) : undefined}
            onDragEnd={onReorder ? onDragEnd : undefined}
            onClick={(e) => {
              if (e.target.closest(".file-list-actions")) return;
              onDownload(file);
            }}
            style={{
              opacity: isDragging ? 0.4 : 1,
              borderTop: isDragOver ? "2px solid #1890ff" : undefined,
            }}
          >
            <div className="file-list-icon">
              <IconComp style={{fontSize: 24, color}} />
            </div>
            <div className="file-list-name">
              {file.name}
              {noExt && (
                <Tooltip title="Fichier sans extension">
                  <WarningOutlined style={{color: "#faad14", marginLeft: 6, fontSize: 14}} />
                </Tooltip>
              )}
            </div>
            <div className="file-list-size">{formatSize(file.size)}</div>
            <div className="file-list-date">{file.modifiedAt ? moment(file.modifiedAt).format("DD/MM/YYYY HH:mm") : "-"}</div>
            <Dropdown overlay={menu} trigger={["click"]}>
              <div className="file-list-actions" onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}>
                <MoreOutlined />
              </div>
            </Dropdown>
          </div>
        );
      })}
    </div>
  );
};

export default FileList;
